import {
    MOBILE_TOP_NAV_HEIGHT_FULL_LOGO,
    useStyles,
    MobileHeaderSection,
    pxToRem,
    pxArrayToRem,
} from '@archipro-design/aria';
import { useEffect, useRef, useState } from 'react';

import * as S from './TitleSection.mobile.style';
import { useBoolean, useScroll } from 'ahooks';
import { motion } from 'framer-motion';

interface TitleSectionProps {
    categoryDescription?: string;
    title: string;
    subtitle: string;
    children?: React.ReactNode;
    showCategoryTitle?: boolean;
}

export const TitleSectionMobile = (props: TitleSectionProps) => {
    const {
        categoryDescription,
        title,
        subtitle,
        children,
        showCategoryTitle = true,
    } = props;

    const filterRef = useRef<HTMLDivElement>(null);

    // Watch the scroll progress to animate the nav (hide/reveal)
    const scroll = useScroll();
    const [lastScrollTop, setLastScrollTop] = useState(0);
    const [filterBarState, setFilterBarState] = useState<{
        isSticky: boolean;
        isVisible: boolean;
    }>({
        isSticky: false,
        isVisible: false,
    });

    const { css } = useStyles(filterBarState);
    const filterBarRef = useRef<HTMLDivElement>(null);

    // Get the position of the filter section.
    const filterContainer =
        filterRef.current
            ?.querySelector('.ui-animation')
            ?.getBoundingClientRect() || null;
    const startStickyFilter = filterContainer ? filterContainer.y : null;
    const [hasReachedFilterBar, { setTrue, setFalse }] = useBoolean(
        startStickyFilter ? startStickyFilter < -54 : false
    );

    // Attach the scroll event listener to the window object.
    useEffect(() => {
        const scrollYValue = scroll?.top || 0 - MOBILE_TOP_NAV_HEIGHT_FULL_LOGO;
        if (filterBarRef.current) {
            const filterBarHeight = filterBarRef.current.offsetHeight;
            const reachedFilterBar =
                startStickyFilter && startStickyFilter < -filterBarHeight;
            const isGoingDown =
                scrollYValue >= 20 && scrollYValue > lastScrollTop;
            const isGoingUp = scrollYValue < lastScrollTop;

            // When pass the filter section, hide the filter bar.
            if (reachedFilterBar) {
                if (isGoingDown) {
                    // Scrolling down
                    if (!hasReachedFilterBar) {
                        // if we haven't reached the filter bar yet, hide it to avoid running the animation, happens on first scroll down the page
                        filterBarRef.current.style.visibility = 'hidden';
                    }
                    // Scrolling down the page, the filter bar is sticky and hidden.
                    setFilterBarState({
                        isSticky: true,
                        isVisible: false,
                    });
                } else if (isGoingUp) {
                    // Scrolling up
                    // Show the filter bar to be able to perform the animation.
                    filterBarRef.current.style.visibility = 'visible';
                    // Scrolling up the page, the filter bar is sticky and visible.
                    setFilterBarState({
                        isSticky: true,
                        isVisible: true,
                    });
                }
                setTrue();
            } else if (
                startStickyFilter &&
                startStickyFilter > MOBILE_TOP_NAV_HEIGHT_FULL_LOGO
            ) {
                // The scroll position is above the filter bar, we reset the hasReachedFilterBar flag to false, the filter bar is not sticky but visible
                setFalse();
                setFilterBarState({
                    isVisible: true,
                    isSticky: false,
                });
            }
        }
        // Save the last scroll position.
        setLastScrollTop(scrollYValue);
    }, [
        startStickyFilter,
        scroll?.top,
        lastScrollTop,
        setTrue,
        hasReachedFilterBar,
        setFalse,
    ]);

    // Define the animation for the nav.
    const navAnimation = {
        y:
            filterBarState.isSticky && !filterBarState.isVisible
                ? `-${pxToRem(MOBILE_TOP_NAV_HEIGHT_FULL_LOGO)}`
                : 0,
        opacity: !filterBarState.isSticky || filterBarState.isVisible ? 1 : 0,
    };

    return (
        <>
            {!showCategoryTitle ? (
                <div className={css(S.PotmMobileNavigationWrapper)}>
                    {categoryDescription && (
                        <div
                            className={css(S.PotmMobileDescriptionWrapper)}
                            dangerouslySetInnerHTML={{
                                __html: categoryDescription,
                            }}
                        ></div>
                    )}
                    <motion.div
                        className={css(S.PotmMobileFilterWrapper)}
                        initial={{ y: 0, opacity: 1 }}
                        animate={navAnimation}
                        transition={{
                            type: 'tween',
                            duration: 0.2,
                            delay: filterBarState.isVisible ? 0.2 : 0,
                        }}
                        ref={filterBarRef}
                    >
                        {children}
                    </motion.div>
                </div>
            ) : (
                <MobileHeaderSection
                    ref={filterRef}
                    className={css(S.MobileProfileNavigationWrapper)}
                    title={{
                        maximumLines: 1,
                        children: title,
                    }}
                    subtitle={subtitle}
                    {...(categoryDescription && {
                        description: {
                            children: (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: categoryDescription,
                                    }}
                                ></div>
                            ),
                        },
                    })}
                    content={
                        <motion.div
                            className={css(S.MobileProfileNavigationContainer)}
                            initial={{ y: 0, opacity: 1 }}
                            animate={navAnimation}
                            transition={{
                                type: 'tween',
                                duration: 0.2,
                                delay: filterBarState.isVisible ? 0.2 : 0,
                            }}
                            ref={filterBarRef}
                        >
                            {children}
                        </motion.div>
                    }
                    variables={{
                        contentPadding: pxArrayToRem([23, 18]),
                    }}
                />
            )}
        </>
    );
};
